/**
 * Corrective Action Request report (SPEC §8.7, §8.8).
 *
 * Reading order is trend -> driver -> record: the monthly chart sets context,
 * the vendor matrix says who is driving it, and the detail table holds the
 * individual CARs for the selected month.
 *
 * This is the reference implementation's `CarReport.tsx` with its data layer
 * removed rather than rewritten. What went:
 *
 * * **Four `useEffect` fetches and their cancellation flags.** Every panel comes
 *   from one payload, so there is nothing to sequence and nothing to cancel. Both
 *   defects found in the original's review were a panel not depending on
 *   everything that changes it — the stale vendor matrix and the wrong site's
 *   undated count. Neither is expressible here: there are no dependency arrays.
 * * **`useState` for the filters and the selected month.** They live in the URL
 *   (§9.5), so a drilled-down view is a link — which the original explicitly
 *   could not do (brief §8, item 3).
 * * **`FilterBar`.** The portal's ParamBar renders the same three pickers from
 *   the manifest, filled from this payload's own options datasets with the same
 *   row counts in their labels.
 * * **The page header, the freshness line and the undated-CAR sentence.** The
 *   portal renders the title, the "read from source" badge with its refresh
 *   control, and the caveat (§9.3, §9.6). Repeating any of them here would be two
 *   copies of one figure.
 *
 * What stayed is everything the reader sees: the column chart, the sequential
 * matrix with its clickable vendors, and the expand-in-place detail table.
 */

import type { CustomReportProps } from '..'
import { Card } from '../shared/Card'
import { MonthColumnChart } from '../shared/MonthColumnChart'
import { monthLabel } from '../shared/format'
import { count, dataset, text } from '../shared/rows'

import '../report.css'

import { CategoryBreakdown } from './CategoryBreakdown'
import { CategoryStackChart, type FamilyPoint } from './CategoryStackChart'
import { DetailTable } from './DetailTable'
import { VendorMatrix } from './VendorMatrix'

/** Matches the manifest's sentinel for an unset filter. */
const ALL = '(all)'

/** "Catapult Print (14)" -> "Catapult Print". */
function withoutCount(label: string): string {
  return label.replace(/\s+\(\d+\)$/, '')
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function CorrectiveActions({ result, values, setParam }: CustomReportProps) {
  const monthly = dataset(result, 'monthly').map((row) => ({
    month: text(row.month) ?? '',
    value: count(row.car_count),
  }))
  const detail = dataset(result, 'primary')
  const familyRows = dataset(result, 'family_summary')
  const categoryRows = dataset(result, 'category_summary')
  const monthlyFamily: FamilyPoint[] = dataset(result, 'monthly_family').map((row) => ({
    month: text(row.month) ?? '',
    family: text(row.family) ?? '',
    count: count(row.car_count),
  }))
  const matrixRows = dataset(result, 'vendor_matrix')
  const matrixMonths = dataset(result, 'matrix_months').map((row) => text(row.month) ?? '')

  // The selected month comes from the server, not from a local fallback. The
  // server already had to resolve it — the detail rows and the matrix window are
  // computed from it — so re-deriving it here would be a second implementation
  // of the same rule, and the two would disagree the first time a filter
  // excluded the month in the URL.
  const selectedMonth = text(dataset(result, 'selection')[0]?.month ?? null)

  const total = monthly.reduce((sum, m) => sum + m.value, 0)

  const vendor = asString(values.vendor)
  const status = asString(values.status)
  const location = asString(values.location)

  const vendorLabel = dataset(result, 'vendors').find((row) => text(row.value) === vendor)
  const vendorName =
    vendor === ALL || vendorLabel === undefined
      ? null
      : withoutCount(text(vendorLabel.label) ?? vendor)

  return (
    <div className="bespoke-report">
      <p className="report-summary">
        {total} {total === 1 ? 'CAR' : 'CARs'}
        {vendorName === null ? ' across all vendors' : ` for ${vendorName}`}
        {status === ALL || status === '' ? '' : ` · ${status}`}
        {location === ALL || location === '' ? '' : ` · ${location}`}
      </p>

      <Card title="CARs by month" subtitle="Select a month to load its detail below">
        <MonthColumnChart
          data={monthly}
          selectedMonth={selectedMonth}
          onSelectMonth={(month) => {
            setParam('month', month)
          }}
          noun={['CAR', 'CARs']}
          emptyLabel="No CARs match these filters."
        />
      </Card>

      <Card
        title="CARs by month and category"
        subtitle="Stacked by category family. Select a month to load its breakdown and detail below."
      >
        <CategoryStackChart
          rows={monthlyFamily}
          selectedMonth={selectedMonth}
          onSelectMonth={(month) => {
            setParam('month', month)
          }}
          emptyLabel="No categorised CARs match these filters."
        />
      </Card>

      <Card
        title="CARs by vendor — trailing 6 months"
        subtitle={
          selectedMonth === null
            ? undefined
            : `Six months ending ${monthLabel(selectedMonth)} · select a vendor to filter the report`
        }
      >
        <VendorMatrix
          rows={matrixRows}
          months={matrixMonths}
          onSelectVendor={(supplierId) => {
            setParam('vendor', supplierId)
          }}
        />
      </Card>

      <Card
        title="CAR detail"
        subtitle={
          selectedMonth === null
            ? 'No month to show'
            : `${monthLabel(selectedMonth)} · ${detail.length.toString()} ${
                detail.length === 1 ? 'record' : 'records'
              } · select a row to see the full complaint and corrective action`
        }
      >
        <DetailTable rows={detail} />
      </Card>

      <CategoryBreakdown
        familyRows={familyRows}
        categoryRows={categoryRows}
        detailRows={detail}
        selectedMonth={selectedMonth}
      />
    </div>
  )
}
